import React, { KeyboardEventHandler } from 'react';
import {
  components, GroupBase, ValueContainerProps,
  MultiValueProps,
  MultiValue,
  ActionMeta,
  InputActionMeta,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FrontendFilterValue } from '../../types/filters.type';
import { ReactSelectAction } from '../../types/enums';
import { reactElementIsType } from '../../utils/helpers';
import CustomOption from './customOption';
import Chip from '../chip';
import { DelimiterRules, PasteValueHandler } from '../../utils/pasteValueHandler';
import { KeyCode } from '../../utils/constants';

const { ValueContainer, Placeholder } = components;

interface InputCreateParams {
  label: string,
  labelId: string,
  placeholder: string,
  value: FrontendFilterValue[],
  setValue: (value: FrontendFilterValue[]) => void,
  delimiterRules: DelimiterRules
}

interface CustomValueContainerProps extends ValueContainerProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>> {
  children: React.ReactNode;
}

// creates custom value container that displays number of selected items
function CustomValueContainer({ children, ...props }: Readonly<CustomValueContainerProps>) {
  const { selectProps, hasValue } = props;
  const { inputValue, placeholder } = selectProps;
  return (
    <ValueContainer {...props}>
      {React.Children.map(children, (child) => (!reactElementIsType(child, Placeholder) ? child : null))}

      {/* Show original placeholder when no selections and no input */}
      {!hasValue && !inputValue && (
        <span className="absolute left-0 text-xs text-pipgrey pointer-events-none">
          {placeholder}
        </span>
      )}
    </ValueContainer>
  );
}
function CustomMultiValue(props: MultiValueProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>>) {
  const { data, removeProps } = props;
  return (
    <span className="m-0.5">
      <Chip closeButton text={data.displayName} colour="green" onClick={removeProps.onClick} small />
    </span>
  );
}

export default function InputCreate({
  label, labelId, placeholder, value, setValue, delimiterRules
}: Readonly<InputCreateParams>) {
  const pasteHandler = new PasteValueHandler(delimiterRules);
  const [inputValue, setInputValue] = React.useState('');
  const createOption = (text: string) => ({
    id: text,
    displayName: text,
  });
  const addOptions = (newOptions: FrontendFilterValue[]) => {
    const existing = new Set(value.map((option) => option.displayName));
    setValue([
      ...value,
      ...newOptions.filter((option) => option.displayName && !existing.has(option.displayName)),
    ]);
  };
  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!inputValue) return;

    switch (event.key) {
      case KeyCode.Enter:
      case KeyCode.Tab:
        createOption(inputValue);
        addOptions([createOption(inputValue)]);
        setInputValue('');
        event.preventDefault();
        break;
      case KeyCode.Space:
        if (delimiterRules === DelimiterRules.SpacesAreNotDelimiter) {
          event.preventDefault();
          setInputValue(`${inputValue} `);
        }
        break;
      default:
        break;
    }
  };
  const onInputChange = (newValue: string, action: InputActionMeta) => {
    if (action.action === ReactSelectAction.InputChange) {
      if (pasteHandler.isPastedValue(newValue)) {
        const pastedValues = pasteHandler.createPastedValues(newValue);
        const newOptions = pastedValues
          .filter((v) => v.trim() !== '')
          .map((pastedValue) => createOption(pastedValue));
        addOptions(newOptions);
        setInputValue('');
      } else {
        setInputValue(newValue);
      }
    }
  };

  const handleChange = (
    selectedOptionsProps: MultiValue<FrontendFilterValue>,
    action: ActionMeta<FrontendFilterValue>,
  ) => {
    if (action.action === ReactSelectAction.Clear || action.action === ReactSelectAction.SelectOption) {
      setInputValue('');
    }
    const values = selectedOptionsProps ? selectedOptionsProps.map((option) => option) : [];
    setValue(values);
  };

  const selectedCount = value.length > 0 ? ` (${value.length})` : '';

  return (
    <label htmlFor={`multi-select-${labelId}`} className="">
      <div className="pb-3 text-label text-secondary ">{`${label}${selectedCount}`}</div>
      <CreatableSelect
        inputId={`multi-select-${labelId}`}
        isMulti
        isClearable
        getOptionValue={(option) => option.displayName}
        placeholder={placeholder}
        classNames={{
          clearIndicator: () => 'cursor-pointer',
          valueContainer: () => 'relative flex flex-wrap w-full items-center py-1.5 ml-2',
          control: () => '!flex !p-0 w-full',
          container: ({ isFocused }) => `w-full flex! p-0! max-w-[400px] max-h-[400px] ${isFocused ? 'input-focused' : 'input-default'} rounded-lg overflow-scroll`,

        }}
        styles={{ // keep basic default styles so not all custom
          control: () => ({}),
          option: () => ({}),
          valueContainer: () => ({}),
          container: () => ({}),

        }}
        inputValue={inputValue}
        value={value}
        menuIsOpen={false}
        hideSelectedOptions={false}
        onInputChange={onInputChange}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        components={{
          MultiValue: CustomMultiValue,
          ValueContainer: CustomValueContainer,
          Option: CustomOption,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
      />
    </label>
  );
}
